import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { message } from 'antd';
import { captureException } from 'utils/errors';
import { POSHUB_MENU_DRIVER } from '../constants';
import { usePosHubIntegration } from '../PosHubIntegrationProvider';
import { MenuContext } from './context';
import { fetchMenusById, fetchMenusByMerchantId } from './requests';

function MenuProvider({ children }) {
  const { id: merchantId } = useParams();
  const { posHubIntegrated, posHubStatusLoading } = usePosHubIntegration();
  const [messageApi, contextHolder] = message.useMessage();
  const [allMenus, setAllMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState();
  const [menuDetail, setMenuDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState();

  const menus = useMemo(
    () => (posHubIntegrated
      ? allMenus.filter((m) => m.migration_driver === POSHUB_MENU_DRIVER)
      : allMenus),
    [posHubIntegrated, allMenus],
  );

  const effectiveLoading = loading || posHubStatusLoading;

  useEffect(() => {
    setLoading(true);
    fetchMenusByMerchantId(merchantId).then((data) => {
      setAllMenus(data.results ?? []);
    }).catch((e) => {
      captureException(e);
    }).finally(() => {
      setLoading(false);
    });
  }, [merchantId]);

  useEffect(() => {
    if (!menus.length) {
      setSelectedMenu(undefined);
      setMenuDetail({});
      return;
    }
    setSelectedMenu((prev) => {
      if (prev && menus.some((m) => m.id === prev)) return prev;
      return menus[0].id;
    });
  }, [menus]);

  useEffect(() => {
    if (selectedMenu) fetchMenuDetail();
  }, [selectedMenu]);

  const fetchMenuDetail = () => {
    setItemsLoading(true);
    fetchMenusById(selectedMenu).then((data) => {
      setMenuDetail(data);
    }).catch((e) => {
      captureException(e);
    }).finally(() => {
      setItemsLoading(false);
    });
  };

  const fetchMenus = () => {
    setLoading(true);
    fetchMenusByMerchantId(merchantId).then((data) => {
      setAllMenus(data.results ?? []);
    }).catch((e) => {
      captureException(e);
    }).finally(() => {
      setLoading(false);
    });
  };

  const showMessage = ({ type, content }) => {
    const text = content || (type === 'error' && 'Something went wrong! please try again');
    if (!text) return;
    messageApi.open({ type, content: text });
  };
  const contextValue = useMemo(() => ({
    menus,
    selectedMenu,
    setSelectedMenu,
    menuDetail,
    setMenuDetail,
    loading: effectiveLoading,
    setLoading,
    fetchMenus,
    fetchMenuDetail,
    itemsLoading,
    setItemsLoading,
    showMessage,
    searchTerm,
    setSearchTerm,
    posHubIntegrated,
  }), [
    menus,
    selectedMenu,
    setSelectedMenu,
    menuDetail,
    setMenuDetail,
    effectiveLoading,
    fetchMenus,
    fetchMenuDetail,
    itemsLoading,
    setItemsLoading,
    showMessage,
    searchTerm,
    setSearchTerm,
    posHubIntegrated,
  ]);

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
      {contextHolder}
    </MenuContext.Provider>
  );
}

export default MenuProvider;
